import { WizardData } from '../ProbateWizard';
import { ProbateResponse, EstateAssets, EstateProfile, IHTCalculation } from '../../types';
import { assessIHTRiskFlags } from '../../server/services/ihtRiskFlags';
import { assessProbateRisks } from '../../server/services/probateRiskDetector';
import { generateEvidenceRequests, EvidenceCategory, EvidenceRequest } from '../../server/services/evidenceGenerator';
import { generateFormMappings, FormMapping } from '../../server/services/formMappingService';
import { assessComplexity, ComplexityLevel } from '../../server/services/probateComplexityScorer';
import { selectReportPanels, ReportPanels } from '../results/selectReportPanels';
import { TRUST_ESTATE_PROVISIONAL, TRUST_ESTATE_ZERO_TAX } from '../results/trustEstateProvisional';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Use explicit Unicode £ (U+00A3) + manual number formatting to avoid encoding artifacts
const fmt = (n: number | null | undefined, decimals = 0) => {
    if (n == null) return '\u2014'; // em-dash
    const abs = Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return (n < 0 ? '-\u00A3' : '\u00A3') + abs;
};

const fmtDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'Unknown';
    try { return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return dateStr; }
};

const domicileLabel = (v?: string) =>
    v === 'UK' ? 'United Kingdom' : v === 'NON_UK' ? 'Non-UK' : v === 'UNSURE' ? 'Not confirmed' : 'Unknown';

// Minimal HTML escape for user-supplied strings interpolated into the report
// template. Intentionally narrow — covers only Phase 1.5 additions; existing
// usages are not retroactively changed in this pass.
const escapeHtml = (s: string): string =>
    s.replace(/[&<>"']/g, (ch) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[ch]!));

const maritalLabel = (v?: string) => ({
    MARRIED_OR_CP: 'Married / Civil Partnership',
    WIDOWED: 'Widowed',
    DIVORCED_OR_SEPARATED: 'Divorced / Separated',
    NEVER_MARRIED: 'Never Married',
} as Record<string, string>)[v ?? ''] ?? 'Unknown';

const ownershipLabel = (v?: string) => ({
    sole: 'Sole ownership',
    joint_tenants: 'Joint tenants',
    tenants_in_common: 'Tenants in common',
} as Record<string, string>)[v ?? ''] ?? (v ?? 'Unknown');

// ---------------------------------------------------------------------------
// Pro report metadata
// ---------------------------------------------------------------------------
// Only the Pro report route passes this. The consumer call-site does not,
// so consumer output is byte-for-byte unchanged. Every field is optional —
// a Pro user is allowed to print before all metadata is filled in, and we
// must not crash on missing fields.

export interface ProReportMetadata {
    client_name?: string | null;
    deceased_name?: string | null;
    executor_name?: string | null;
    matter_reference?: string | null;
    /**
     * Caller-supplied prepared date. Defaults to "today" if omitted, so the
     * Pro report always shows a date even when the case row has no
     * explicit prepared_at field yet.
     */
    prepared_date?: Date | string | null;
}

/**
 * Render the Pro case header block. Returns an HTML string ready to splice
 * into the report template, or '' if no usable metadata was provided so
 * the consumer layout is unaffected.
 *
 * Exported so tests can pin the contract (consumer call → '', Pro call →
 * marked HTML containing the supplied fields) without spinning up a DOM.
 */
export function buildProCaseHeader(meta: ProReportMetadata | null | undefined): string {
    if (!meta) return '';

    const rows: Array<[string, string]> = [];
    const add = (label: string, value: string | null | undefined) => {
        const v = (value ?? '').toString().trim();
        if (v) rows.push([label, escapeHtml(v)]);
    };
    add('Client', meta.client_name);
    add('Deceased', meta.deceased_name);
    add('Executor', meta.executor_name);
    add('Matter reference', meta.matter_reference);

    // Prepared date: use caller-supplied if present, else today. Always shown
    // so the printed sheet carries a date even when other fields are blank.
    // `new Date('not-a-date')` returns an Invalid Date (rather than throwing),
    // so detect that explicitly and fall back to today.
    const fmtPrep = (d: Date) =>
        d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    let preparedStr: string;
    try {
        let d: Date;
        if (meta.prepared_date instanceof Date) {
            d = meta.prepared_date;
        } else if (meta.prepared_date) {
            d = new Date(meta.prepared_date);
        } else {
            d = new Date();
        }
        if (isNaN(d.getTime())) d = new Date();
        preparedStr = fmtPrep(d);
    } catch {
        preparedStr = fmtPrep(new Date());
    }
    rows.push(['Prepared', escapeHtml(preparedStr)]);

    // If even after defaulting we have no rows at all (impossible — prepared
    // is always added — but defensive), suppress the block entirely.
    if (rows.length === 0) return '';

    const rowsHtml = rows
        .map(([label, value]) => `
            <tr>
                <th>${escapeHtml(label)}</th>
                <td>${value}</td>
            </tr>
        `)
        .join('');

    return `
    <section class="pro-case-header" data-pro-metadata="true" aria-label="Professional case header">
        <div class="pro-case-header__eyebrow">EstateClarity Pro &mdash; Case file</div>
        <table class="pro-case-header__table">
            <tbody>${rowsHtml}</tbody>
        </table>
    </section>
    `;
}

// ---------------------------------------------------------------------------
// HTML sections
// ---------------------------------------------------------------------------

function section(title: string, content: string, pageMarker?: string) {
    const marker = pageMarker
        ? `<div class="page-marker">${pageMarker}</div>`
        : '';
    return `
        <div class="section">
            ${marker}
            <h2>${title}</h2>
            ${content}
        </div>`;
}

function keyValueGrid(rows: [string, string][], cols = 2) {
    const cells = rows.map(([k, v]) => `
        <div class="kv-cell">
            <div class="kv-label">${k}</div>
            <div class="kv-value">${v}</div>
        </div>`).join('');
    return `<div class="kv-grid" style="grid-template-columns: repeat(${cols}, 1fr);">${cells}</div>`;
}

function calcTable(rows: { label: string; value: string; indent?: boolean; bold?: boolean; separator?: boolean; positive?: boolean; doubleRule?: boolean }[]) {
    const trs = rows.map((r, i) => {
        const sepRow = r.separator ? `<tr class="sep-row"><td colspan="2"><div class="rule"></div></td></tr>` : '';
        const doubleRow = r.doubleRule ? `<tr class="sep-row"><td colspan="2"><div class="double-rule"></div></td></tr>` : '';
        const tdClass = [
            r.indent ? 'indent' : '',
            r.bold ? 'bold-cell' : '',
        ].join(' ');
        const valClass = [
            'val-cell',
            r.positive ? 'positive' : '',
            r.bold ? 'bold-cell' : '',
        ].join(' ');
        return `${sepRow}<tr class="${r.bold ? 'bold-row' : ''}"><td class="${tdClass}">${r.label}</td><td class="${valClass}">${r.value}</td></tr>${doubleRow}`;
    }).join('');
    return `<table class="calc-table"><tbody>${trs}</tbody></table>`;
}

// ---------------------------------------------------------------------------
// 1. ESTATE OVERVIEW (new executive summary)
// ---------------------------------------------------------------------------

function buildEstateOverviewSection(data: WizardData, response: ProbateResponse | null): string {
    const ihtCalc = (response?.derived as any)?.iht_calc as IHTCalculation | undefined;
    const actionPack = response?.actionPack;
    const assets = data.EstateAssets;
    const routeKey = actionPack?.recommended_pathway ?? 'UNKNOWN';

    const routeLabels: Record<string, string> = {
        POST_2022_EXCEPTED: 'Excepted estate \u2014 simplified route',
        POST_2022_IHT400: 'Full IHT400 return',
        NEEDS_MORE_INFO: 'To be confirmed',
        UNKNOWN: 'To be confirmed',
    };
    const routeLabel = routeLabels[routeKey] ?? routeKey.replace(/_/g, ' ');

    const taxDue = ihtCalc?.total_tax_due;
    const netEstate = ihtCalc?.breakdown?.net_value;
    const afterTax = (netEstate != null && taxDue != null) ? Math.max(0, netEstate - taxDue) : null;
    // WP1: trust property may be missing — the figures are printed as computed but labelled provisional.
    const provisional = !!ihtCalc?.breakdown?.trust_estate_inclusion_gap;
    const prov = (v: string) => provisional ? `${v} (provisional)` : v;

    // Probate status
    const props = assets.properties ?? [];
    const hasTic = props.some(p => p.ownership_type === 'tenants_in_common');
    const hasSole = props.some(p => p.ownership_type === 'sole');
    let probateLabel = 'To be confirmed';
    if (hasTic || hasSole) probateLabel = 'Yes \u2014 likely required';
    else if (props.some(p => p.ownership_type === 'joint_tenants')) probateLabel = 'Unlikely';

    const rows: [string, string][] = [
        ['Estimated IHT', taxDue === 0 ? (provisional ? prov('None calculated') : 'None expected') : taxDue != null ? prov(fmt(taxDue)) : 'Pending'],
        ['Estimated estate after tax', afterTax != null ? prov(fmt(afterTax)) : 'Pending'],
        ['Net estate', netEstate != null ? fmt(netEstate) : 'Pending'],
        ['Probate required', probateLabel],
        ['Filing route', routeLabel],
    ];

    // Build narrative summary
    let narrative = '';
    if (provisional && taxDue != null && taxDue > 0 && ihtCalc?.breakdown?.taxable_value != null) {
        narrative = `<p class="overview-narrative">On the information currently entered, the estate exceeds the available tax-free allowances by approximately ${fmt(ihtCalc.breakdown.taxable_value)}, giving inheritance tax of ${fmt(taxDue)}. ${TRUST_ESTATE_PROVISIONAL}</p>`;
    } else if (provisional && taxDue === 0) {
        narrative = `<p class="overview-narrative">${TRUST_ESTATE_ZERO_TAX} ${TRUST_ESTATE_PROVISIONAL}</p>`;
    } else if (taxDue != null && taxDue > 0 && ihtCalc?.breakdown?.taxable_value != null) {
        narrative = `<p class="overview-narrative">The estate exceeds the available tax-free allowances by approximately ${fmt(ihtCalc.breakdown.taxable_value)}, producing an estimated inheritance tax liability of ${fmt(taxDue)}.</p>`;
    } else if (taxDue === 0) {
        narrative = `<p class="overview-narrative">Based on the information provided, no inheritance tax is expected. The estate falls within the available tax-free allowances.</p>`;
    } else {
        narrative = `<p class="overview-narrative">The tax position cannot be fully determined until all outstanding information has been provided.</p>`;
    }

    return section('Estate Overview', `
        <div class="overview-grid">
            ${rows.map(([k, v]) => `
                <div class="overview-cell">
                    <div class="overview-label">${k}</div>
                    <div class="overview-value">${v}</div>
                </div>`).join('')}
        </div>
        ${narrative}
    `, 'Estate Overview');
}

// ---------------------------------------------------------------------------
// Date-of-death outside supported range — banner
// ---------------------------------------------------------------------------
// Mirrors the on-screen IHTCalculatorPanel banner. Driven by the same selector
// so the report and the app agree on which regime applies and whether figures
// may be shown at all (see selectReportPanels / showFigures).

function buildUnsupportedDateBanner(panels: ReportPanels): string {
    if (!panels.showBanner) return '';
    const copy = panels.bannerKind === 'pre_iht'
        ? 'This estate has a date of death before 18 March 1986, when Inheritance Tax did not yet exist — Capital Transfer Tax or Estate Duty applied instead. This tool cannot compute that regime, so no tax figures are shown. Please seek professional advice.'
        : 'This estate has a date of death before 6 April 2017. The figures and HMRC references shown are indicative only — the residence nil rate band and some current rules did not yet apply on that date. Please seek professional advice before relying on this estimate.';
    return section('Date of Death Outside Supported Range', `
        <div class="status-box" style="background:#fffbeb;border-color:#fde68a;">
            <p class="status-reason" style="color:#92400e;">${copy}</p>
        </div>`);
}

// ---------------------------------------------------------------------------
// WP1 — trust property may be missing from the figures (provisional) — banner
// ---------------------------------------------------------------------------
// Mirrors the on-screen TrustEstateInclusionNotice: the engine's own
// 'trust_estate_inclusion' note, shown with the figures. Presentation only.

function buildTrustEstateInclusionBanner(ihtCalc: IHTCalculation | undefined): string {
    if (!ihtCalc?.breakdown?.trust_estate_inclusion_gap) return '';
    const notes = (ihtCalc.confidence?.flags ?? [])
        .filter(f => f.field === 'trust_estate_inclusion')
        .map(f => `<p class="status-reason" style="color:#92400e;">${f.note}</p>`)
        .join('');
    return section('Specialist IHT Advice Recommended', `
        <div class="status-box" style="background:#fffbeb;border-color:#fde68a;">
            <div class="status-label" style="color:#92400e;">Trust property may be missing &mdash; figures are provisional</div>
            <p class="status-reason" style="color:#92400e;">The figures in this report are provisional, not a final tax amount.</p>
            ${notes}
        </div>`);
}

// ---------------------------------------------------------------------------
// Build each section from data (existing sections preserved)
// ---------------------------------------------------------------------------

function buildDeceasedSection(data: WizardData): string {
    const d = data.EstateProfile.deceased || {};
    const rows: [string, string][] = [
        ['Date of Death', fmtDate(d.date_of_death)],
        ['Domicile', domicileLabel(d.domicile)],
        ['Marital Status', maritalLabel(d.maritalStatus)],
    ];
    if (d.firstName || d.lastName) {
        rows.unshift(['Full Name', `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim()]);
    }
    return section('Deceased Details', keyValueGrid(rows, 3));
}

function buildIHTSummarySection(data: WizardData, response: ProbateResponse | null): string {
    const ihtCalc = (response?.derived as any)?.iht_calc;
    const actionPack = response?.actionPack;
    const routeKey = actionPack?.recommended_pathway ?? 'UNKNOWN';

    const routeLabels: Record<string, string> = {
        POST_2022_EXCEPTED: 'Excepted Estate \u2014 no IHT return required',
        POST_2022_IHT400: 'Full IHT400 return required',
        NEEDS_MORE_INFO: 'More information needed',
        UNKNOWN: 'Not yet determined',
    };
    const routeLabel = routeLabels[routeKey] ?? routeKey.replace(/_/g, ' ');

    const taxDue = ihtCalc?.total_tax_due;
    const provisional = !!ihtCalc?.breakdown?.trust_estate_inclusion_gap;
    const taxDisplay = provisional
        ? (taxDue === 0 ? 'None calculated (provisional)' : taxDue != null ? `${fmt(taxDue)} (provisional)` : 'Pending inputs')
        : taxDue === 0 ? 'No tax expected' : taxDue != null ? fmt(taxDue) : 'Pending inputs';

    const rows: [string, string][] = [
        ['Recommended Route', routeLabel],
        ['Estimated Tax Due', taxDisplay],
    ];

    if (ihtCalc?.breakdown?.tax_year) rows.push(['Tax Year', ihtCalc.breakdown.tax_year]);
    if (ihtCalc?.breakdown?.net_value != null) rows.push(['Net Estate', fmt(ihtCalc.breakdown.net_value)]);

    let note = '';
    if (provisional) {
        note += `<p class="note">${TRUST_ESTATE_PROVISIONAL}</p>`;
    }
    if (routeKey === 'POST_2022_IHT400') {
        note += '<p class="note">A full return does not necessarily mean tax is payable \u2014 it means HMRC requires more detailed information about the estate.</p>';
    }

    return section('Inheritance Tax Summary', keyValueGrid(rows, 2) + note, 'Tax &amp; Filing Summary');
}

function buildCalcBreakdownSection(data: WizardData, response: ProbateResponse | null): string {
    const ihtCalc = (response?.derived as any)?.iht_calc;
    if (!ihtCalc?.breakdown) return '';
    const b = ihtCalc.breakdown;

    const fmtSigned = (n: number) => n === 0 ? '\u2014' : `-${fmt(n)}`;

    const rows: Parameters<typeof calcTable>[0] = [];

    // Gross → Net
    if (b.gross_value != null) rows.push({ label: 'Estate before debts', value: fmt(b.gross_value) });
    if ((b.debts_total ?? 0) > 0) rows.push({ label: 'Debts and liabilities', value: fmtSigned(b.debts_total!), indent: true });
    if (b.net_value != null) rows.push({ label: 'Net estate after debts', value: fmt(b.net_value), bold: true, separator: true });

    // Exemptions (only show if they exist)
    const hasExemptions = (b.bpr_relief_total ?? 0) > 0 || (b.apr_relief_total ?? 0) > 0 ||
        (b.spouse_exemption_total ?? 0) > 0 || (b.charity_exemption_total ?? 0) > 0;
    if (hasExemptions) {
        if ((b.bpr_relief_total ?? 0) > 0) rows.push({ label: 'Business Property Relief (BPR)', value: fmtSigned(b.bpr_relief_total!), indent: true, positive: true });
        if ((b.apr_relief_total ?? 0) > 0) rows.push({ label: 'Agricultural Property Relief (APR)', value: fmtSigned(b.apr_relief_total!), indent: true, positive: true });
        if ((b.spouse_exemption_total ?? 0) > 0) rows.push({ label: 'Spouse / civil partner exemption', value: fmtSigned(b.spouse_exemption_total!), indent: true, positive: true });
        if ((b.charity_exemption_total ?? 0) > 0) rows.push({ label: 'Charitable exemption', value: fmtSigned(b.charity_exemption_total!), indent: true, positive: true });
    }

    // Total allowances applied
    const totalAllowance = b.total_allowance ?? 0;
    if (totalAllowance > 0) {
        rows.push({ label: 'Total allowances applied', value: fmt(totalAllowance), bold: true, separator: true });

        // NRB — show full gift derivation when gifts reduce the band
        if (b.nrb_available != null) {
            const giftsUsed = b.gifts_chargeable_total ?? 0;
            if (giftsUsed > 0) {
                const nrbBase = b.nrb_available + giftsUsed;
                // Calculate raw gift total from estate data so we can show exemptions
                const rawGiftTotal = (data.EstateAssets.lifetime_gifts ?? [])
                    .reduce((s: number, g: any) => s + (g.amount || 0), 0);
                const exemptionsApplied = Math.max(0, rawGiftTotal - giftsUsed);

                rows.push({ label: 'Lifetime gifts within 7 years', value: fmt(rawGiftTotal || giftsUsed), indent: true });
                if (exemptionsApplied > 0) {
                    rows.push({ label: 'Less: annual gift exemptions applied', value: fmtSigned(exemptionsApplied), indent: true, positive: true });
                }
                rows.push({ label: 'Chargeable gifts', value: fmt(giftsUsed), indent: true, bold: true });
                rows.push({ label: 'Nil Rate Band', value: fmt(nrbBase), indent: true });
                rows.push({ label: 'Less: used by chargeable gifts', value: fmtSigned(giftsUsed), indent: true });
                rows.push({ label: 'Available Nil Rate Band', value: fmt(b.nrb_available), indent: true, bold: true });
            } else {
                rows.push({ label: 'Nil Rate Band', value: fmt(b.nrb_available), indent: true });
            }
        }
        if ((b.rnrb_available ?? 0) > 0) {
            rows.push({ label: 'Residence Nil Rate Band', value: fmt(b.rnrb_available!), indent: true });
        }
    }

    // Taxable estate
    if (b.taxable_value != null) rows.push({ label: 'Taxable estate', value: fmt(b.taxable_value), bold: true, separator: true });

    // Final tax line
    const taxDue = ihtCalc.total_tax_due;
    const rate = b.iht_rate_applied ?? (b as any).tax_rate ?? 0.4;
    rows.push({
        label: `Inheritance tax at ${(rate * 100).toFixed(0)}%${b.charitable_rate_applies ? ' (reduced \u2014 \u226510% to charity)' : ''}${b.trust_estate_inclusion_gap ? ' (provisional)' : ''}`,
        value: taxDue === 0 ? 'Nil' : fmt(taxDue ?? 0),
        bold: true,
        doubleRule: true,
    });

    // Taper relief note
    if ((b.taper_relief_total ?? 0) > 0) {
        rows.push({ label: 'Less: Taper relief on gifts', value: fmtSigned(b.taper_relief_total!), indent: true, positive: true });
        const netTax = Math.max(0, (taxDue ?? 0));
        rows.push({ label: `Net tax due after taper relief${b.trust_estate_inclusion_gap ? ' (provisional)' : ''}`, value: fmt(netTax), bold: true });
    }

    return section('IHT Calculation Breakdown', calcTable(rows));
}

function buildProbateSection(data: WizardData, response: ProbateResponse | null): string {
    // Inline a simple version of the probate logic to avoid importing React components
    const assets = data.EstateAssets;
    const props = assets.properties ?? [];
    const hasTic = props.some(p => p.ownership_type === 'tenants_in_common');
    const hasSole = props.some(p => p.ownership_type === 'sole');

    let status = 'Unclear \u2014 further information needed';
    let reason = 'Ownership details are incomplete or not yet confirmed.';

    if (hasTic) {
        status = 'Probate likely required';
        reason = "At least one property share does not pass automatically to a co-owner. Probate is usually required to deal with the deceased's share of property held as tenants in common.";
    } else if (hasSole) {
        status = 'Probate likely required';
        reason = 'At least one property is solely owned and does not pass automatically. Probate will usually be needed to transfer or sell it.';
    } else if (props.some(p => p.ownership_type === 'joint_tenants')) {
        const hasSoleFinancials = (assets.bank_accounts ?? []).some(b => b.ownership === 'sole') ||
            (assets.investments ?? []).some(i => i.ownership === 'sole');
        if (hasSoleFinancials) {
            status = 'Probate unclear \u2014 depends on asset providers';
            reason = 'Banks and registrars often require probate above certain thresholds (typically \u00A315,000\u2013\u00A350,000), which vary by provider.';
        } else {
            status = 'Probate unlikely';
            reason = 'Assets appear to pass automatically (e.g. joint tenancy property, joint accounts), though individual providers may still ask for confirmation.';
        }
    }

    return section('Probate Status', `
        <div class="status-box">
            <div class="status-label">${status}</div>
            <p class="status-reason">${reason}</p>
        </div>`);
}

function buildTrustArrangementsSection(data: WizardData, response: ProbateResponse | null): string {
    const ipdi = data.EstateProfile.iht_details?.spouse_life_interest_trust;
    const ihtCalc = (response?.derived as any)?.iht_calc as IHTCalculation | undefined;
    const b = ihtCalc?.breakdown as any;
    const trustScreening = data.EstateProfile.iht_details?.trust_screening;
    const hasTrustFlags = trustScreening?.has_trusts === true ||
        trustScreening?.created_or_transferred_trust === true ||
        trustScreening?.benefited_from_trust === true;

    // Only show this section if IPDI is detected, unsure, or general trust flags are present
    if (ipdi?.exists !== 'yes' && ipdi?.exists !== 'unsure' && !hasTrustFlags) return '';

    let content = '';

    // IPDI — spouse life interest trust
    if (ipdi?.exists === 'yes') {
        const ipdiValue = b?.ipdi_spouse_exemption ?? ipdi.estimated_value ?? null;
        content += `
            <div class="status-box" style="margin-bottom:14px;">
                <div class="status-label">Life Interest Trust (IPDI)</div>
                <p class="status-reason">
                    A property trust has been identified in the will. The surviving spouse has a right to benefit
                    from the asset for life (a life interest trust).
                </p>
                <p class="status-reason" style="margin-top:8px;">
                    For inheritance tax purposes, this is treated similarly to leaving the asset directly to the
                    spouse. Spouse exemption has been applied${ipdiValue ? ` to ${fmt(ipdiValue)}` : ''}. No immediate
                    inheritance tax is due on this portion of the estate.
                </p>
                <p class="status-reason" style="margin-top:8px;color:#92400e;">
                    When the surviving spouse dies, these assets are typically included in their estate for
                    inheritance tax purposes.
                </p>
            </div>`;
    }

    // IPDI — unsure
    if (ipdi?.exists === 'unsure') {
        content += `
            <div class="status-box" style="background:#fffbeb;border-color:#fde68a;margin-bottom:14px;">
                <div class="status-label" style="color:#92400e;">Possible Trust Arrangement</div>
                <p class="status-reason">
                    You indicated there may be a trust where a spouse or civil partner has a life interest.
                    This can affect inheritance tax treatment. Consider confirming with a professional whether
                    a life interest trust (IPDI) exists in the will.
                </p>
            </div>`;
    }

    // General trust flags
    if (hasTrustFlags && ipdi?.exists !== 'yes') {
        content += `
            <div class="status-box" style="margin-bottom:14px;">
                <div class="status-label">Trust Involvement Detected</div>
                <p class="status-reason">
                    Trust arrangements have been identified in the estate. Additional trust-related
                    schedules may be required when filing the IHT return. The specific treatment
                    depends on the type of trust involved.
                </p>
                <p class="note" style="margin-top:6px;">
                    Consider seeking specialist advice to confirm the IHT treatment of any trust assets.
                </p>
            </div>`;
    }

    return section('Trusts &amp; Special Arrangements', content);
}

function buildAssetSection(data: WizardData): string {
    const a = data.EstateAssets;
    const rows: string[] = [];

    const propRows = (a.properties ?? []).map(p => {
        const fullVal = Number(p.estimated_value) || 0;
        const sharePct = p.deceased_share_percent ?? 100;
        const mortgage = p.has_mortgage ? (Number(p.mortgage_balance) || 0) : 0;
        const netEquity = Math.max(0, fullVal - mortgage);
        const estateShare = Math.round(netEquity * sharePct / 100);
        return `
        <tr>
            <td>${p.label || p.address || 'Property'}${p.mortgage_lender ? ` <span style="color:#94a3b8;font-size:0.75rem">(Lender: ${p.mortgage_lender})</span>` : ''}</td>
            <td>${ownershipLabel(p.ownership_type)} (${sharePct}%)</td>
            <td class="num-cell">${fmt(fullVal)}</td>
            <td class="num-cell">${mortgage > 0 ? fmt(mortgage) : '\u2014'}</td>
            <td class="num-cell">${fmt(estateShare)}</td>
        </tr>`;
    }).join('');

    const hasMortgages = (a.properties ?? []).some(p => p.has_mortgage && Number(p.mortgage_balance) > 0);
    const hasAssumedShares = (a.properties ?? []).some(p =>
        (p.ownership_type === 'joint_tenants' || p.ownership_type === 'tenants_in_common') &&
        (p.deceased_share_percent === undefined || p.deceased_share_percent === null || p.deceased_share_percent === 50)
    );
    if (propRows) {
        rows.push(`
            <h3 class="asset-type">Properties</h3>
            <table class="asset-table">
                <thead><tr><th>Property</th><th>Ownership</th><th>Full value</th><th>Mortgage</th><th>Estate share</th></tr></thead>
                <tbody>${propRows}</tbody>
            </table>
            ${hasMortgages ? '<p class="note" style="margin-top:4px">Mortgage balances are apportioned according to the deceased\'s ownership share. Estate share = (property value \u2212 mortgage balance) \u00D7 ownership %.</p>' : ''}
            ${hasAssumedShares ? '<p class="note" style="margin-top:4px">Where beneficial ownership was not separately confirmed, the deceased\'s share has been assumed for estimation purposes. This should be verified with title deeds or a declaration of trust.</p>' : ''}`);
    }

    const hasJointBanks = (a.bank_accounts ?? []).some(b => b.ownership === 'joint');
    const bankRows = (a.bank_accounts ?? []).map(b => {
        const fullBal = Number(b.estimated_value) || 0;
        const isJoint = b.ownership === 'joint';
        const estateShare = isJoint ? fullBal / 2 : fullBal;
        return `
        <tr>
            <td>${b.provider || b.label || 'Account'}</td>
            <td>${b.ownership === 'sole' ? 'Sole' : isJoint ? 'Joint' : b.ownership ?? '\u2014'}</td>
            <td class="num-cell">${fmt(fullBal)}</td>
            <td class="num-cell">${isJoint ? fmt(estateShare) : '\u2014'}</td>
        </tr>`;
    }).join('');

    if (bankRows) {
        rows.push(`
            <h3 class="asset-type">Bank Accounts</h3>
            <table class="asset-table">
                <thead><tr><th>Bank</th><th>Ownership</th><th>Full balance</th><th>Estate share</th></tr></thead>
                <tbody>${bankRows}</tbody>
            </table>
            ${hasJointBanks ? '<p class="note" style="margin-top:4px">Where a joint account holder\'s beneficial share was not separately confirmed, the deceased\'s share has been assumed to be 50% for estimation purposes.</p>' : ''}`);
    }

    const hasJointInvestments = (a.investments ?? []).some(i => i.ownership === 'joint');
    const invRows = (a.investments ?? []).map(i => `
        <tr>
            <td>${i.provider || i.label || 'Investment'}</td>
            <td>${i.ownership === 'sole' ? 'Sole' : i.ownership === 'joint' ? 'Joint' : i.ownership ?? '\u2014'}</td>
            <td class="num-cell">${fmt(i.estimated_value)}</td>
        </tr>`).join('');

    if (invRows) {
        rows.push(`
            <h3 class="asset-type">Investments</h3>
            <table class="asset-table">
                <thead><tr><th>Provider</th><th>Ownership</th><th>Est. Value</th></tr></thead>
                <tbody>${invRows}</tbody>
            </table>
            ${hasJointInvestments ? '<p class="note" style="margin-top:4px">Where beneficial ownership of a joint investment was not separately confirmed, the deceased\'s share has been assumed to be 50% for estimation purposes.</p>' : ''}`);
    }

    const penRows = (a.pensions ?? []).map(p => `
        <tr>
            <td>${p.provider || 'Pension'}</td>
            <td>${p.in_estate === false ? 'Outside estate (paid to beneficiaries)' : 'May form part of estate'}</td>
            <td class="num-cell">${fmt(p.estimated_value)}</td>
        </tr>`).join('');

    if (penRows) {
        rows.push(`
            <h3 class="asset-type">Pensions</h3>
            <table class="asset-table">
                <thead><tr><th>Provider</th><th>Status</th><th>Est. Value</th></tr></thead>
                <tbody>${penRows}</tbody>
            </table>`);
    }

    // Life insurance
    if (a.life_insurance?.exists === true) {
        const inTrust = a.life_insurance.written_in_trust === true;
        const insVal = Number(a.life_insurance.estimated_value) || 0;
        rows.push(`
            <h3 class="asset-type">Life Insurance</h3>
            <table class="asset-table">
                <thead><tr><th>Status</th><th>Est. Value</th></tr></thead>
                <tbody><tr>
                    <td>${inTrust ? 'Written in trust \u2014 excluded from estate' : 'Not in trust \u2014 included in estate'}</td>
                    <td class="num-cell">${fmt(insVal)}</td>
                </tr></tbody>
            </table>`);
    }

    if (!rows.length) return '';

    return section('Asset Summary', rows.join(''), 'Assets &amp; Gifts');
}

function buildGiftsSection(data: WizardData): string {
    const gifts = data.EstateAssets.lifetime_gifts ?? [];
    if (!gifts.length) return '';

    const recipientLabel = (t: string) =>
        t === 'spouse' ? 'Spouse / Civil Partner' : t === 'charity' ? 'Charity' : 'Other person';

    // Free-text notes must be HTML-escaped (safety) and cleaned of U+FFFD
    // replacement characters, which appear when source data was decoded with the
    // wrong charset (e.g. a windows-1252 spreadsheet read as UTF-8). Leaving the
    // garbled "\ufffd" glyphs on a document handed to HMRC looks untrustworthy.
    const cleanNote = (s: string): string =>
        escapeHtml(s.replace(/\ufffd/g, '').replace(/[ \t]{2,}/g, ' ').trim());

    const rows = gifts.map(g => `
        <tr>
            <td>${g.date ? fmtDate(g.date + '-01') : '\u2014'}</td>
            <td class="num-cell">${fmt(g.amount)}</td>
            <td>${recipientLabel(g.recipient_type)}</td>
            <td>${g.is_regular_from_income ? 'Yes \u2014 exempt' : 'No'}</td>
            <td>${g.description ? (cleanNote(g.description) || '\u2014') : '\u2014'}</td>
        </tr>`).join('');

    return section('Lifetime Gifts', `
        <p class="note">Gifts made within 7 years of death may reduce the available Nil Rate Band.</p>
        <table class="asset-table">
            <thead><tr><th>Date</th><th>Amount</th><th>Recipient</th><th>Regular from income?</th><th>Notes</th></tr></thead>
            <tbody>${rows}</tbody>
        </table>
        <p class="note" style="margin-top:8px">An annual gift exemption of \u00A33,000 per tax year has been applied where available, including any unused allowance carried forward from the previous year. Gifts marked as regular from income may also qualify for the normal expenditure out of income exemption (IHTA 1984 s21).</p>`);
}

function buildIncomeAssessmentSection(data: WizardData): string {
    const gifts = data.EstateAssets.lifetime_gifts ?? [];
    const regularGifts = gifts.filter(g => g.is_regular_from_income);
    if (!regularGifts.length) return '';

    const ia = data.EstateAssets.income_assessment;
    if (!ia) return '';

    const totalIncome = (ia.pension_income ?? 0) + (ia.employment_income ?? 0) + (ia.rental_income ?? 0) + (ia.investment_income ?? 0) + (ia.other_income ?? 0);
    const livingExpenses = ia.annual_living_expenses ?? 0;
    const surplusIncome = totalIncome - livingExpenses;
    const regularGiftTotal = regularGifts.reduce((sum, g) => sum + (g.amount || 0), 0);

    if (totalIncome === 0 && livingExpenses === 0) return '';

    const qualifies = regularGiftTotal <= surplusIncome && surplusIncome > 0;

    const incomeRows: [string, string][] = [];
    if (ia.pension_income) incomeRows.push(['Pension income', fmt(ia.pension_income)]);
    if (ia.employment_income) incomeRows.push(['Employment income', fmt(ia.employment_income)]);
    if (ia.rental_income) incomeRows.push(['Rental income', fmt(ia.rental_income)]);
    if (ia.investment_income) incomeRows.push(['Investment income (dividends / interest)', fmt(ia.investment_income)]);
    if (ia.other_income) incomeRows.push(['Other income', fmt(ia.other_income)]);

    const tableRows = [
        ...incomeRows.map(([k, v]) => `<tr><td>${k}</td><td class="num-cell">${v}</td></tr>`),
        `<tr class="total-row"><td><strong>Total annual income</strong></td><td class="num-cell"><strong>${fmt(totalIncome)}</strong></td></tr>`,
        `<tr><td>Annual living expenses</td><td class="num-cell" style="color:#dc2626">\u2212${fmt(livingExpenses)}</td></tr>`,
        `<tr class="total-row"><td><strong>Surplus income</strong></td><td class="num-cell"><strong>${fmt(surplusIncome)}</strong></td></tr>`,
        `<tr><td>Total regular gifts</td><td class="num-cell">${fmt(regularGiftTotal)}</td></tr>`,
    ].join('');

    const verdict = qualifies
        ? '<p style="color:#059669;font-weight:bold;margin-top:12px">&#10003; Regular gifts may qualify for the normal expenditure out of income exemption.</p>'
        : '<p style="color:#d97706;font-weight:bold;margin-top:12px">&#9888; Gifts may not qualify as they appear to exceed surplus income.</p>';

    return section('Regular Gifts &amp; Income Assessment', `
        <p class="note">Under IHTA 1984 s21, gifts made as part of normal expenditure out of surplus income may be exempt from IHT.</p>
        <table class="asset-table">
            <thead><tr><th>Item</th><th>Annual amount</th></tr></thead>
            <tbody>${tableRows}</tbody>
        </table>
        ${verdict}
        <p class="note" style="margin-top:8px">Executors may claim this exemption if supported by evidence including bank statements, pension statements, dividend or interest records, and evidence of normal household spending.</p>`);
}

function buildHMRCFormsSection(data: WizardData, response: ProbateResponse | null): string {
    const ihtCalc = (response?.derived as any)?.iht_calc;
    const routeKey = response?.actionPack?.recommended_pathway;
    const assets = data.EstateAssets;
    const profile = data.EstateProfile;
    const b = ihtCalc?.breakdown;
    const sp = profile.iht_details?.predeceased_spouse;
    const isIHT400 = routeKey === 'POST_2022_IHT400';
    const willExists = profile.representatives?.will_exists;

    const forms: { ref: string; description: string }[] = [];

    if (isIHT400) {
        forms.push({ ref: 'IHT400', description: 'Inheritance Tax account' });
        forms.push({ ref: 'IHT421', description: 'Probate summary' });
    }

    if (isIHT400 && assets.properties.length > 0) {
        forms.push({ ref: 'IHT405', description: 'Houses, land and buildings' });
    }

    if (isIHT400 && assets.bank_accounts.length > 0) {
        forms.push({ ref: 'IHT406', description: 'Bank and building society accounts' });
    }

    const hasJoint = assets.properties.some(p => p.ownership_type === 'joint_tenants' || p.ownership_type === 'tenants_in_common') ||
        assets.bank_accounts.some(b => b.ownership === 'joint') ||
        assets.investments.some(i => i.ownership === 'joint');
    if (isIHT400 && hasJoint) {
        forms.push({ ref: 'IHT404', description: 'Jointly owned assets' });
    }

    if (isIHT400 && ((assets.lifetime_gifts ?? []).length > 0 || assets.gifts_last_7_years?.exists === true)) {
        forms.push({ ref: 'IHT403', description: 'Gifts and other transfers of value' });
    }

    if (isIHT400 && assets.investments.length > 0) {
        forms.push({ ref: 'IHT411', description: 'Listed stocks and shares (if applicable)' });
    }

    if (isIHT400 && assets.foreign_assets?.exists === true) {
        forms.push({ ref: 'IHT417', description: 'Foreign assets' });
    }

    if (isIHT400 && (assets.trusts_or_life_interest?.exists === true || profile.iht_details?.trust_screening?.has_trusts === true)) {
        forms.push({ ref: 'IHT418', description: 'Assets held in trust' });
    } else if (isIHT400 && assets.trusts_or_life_interest?.unsure === true) {
        // Phase 1.5 — uncertain trust involvement: surface IHT418 as a review item.
        forms.push({ ref: 'IHT418', description: 'Assets held in trust — trust involvement is uncertain; check whether IHT418 or specialist advice is required' });
    }

    if (isIHT400 && sp?.exists === true) {
        forms.push({ ref: 'IHT402', description: 'Claim to transfer unused nil rate band' });
    }

    if (isIHT400 && (b?.rnrb_available ?? 0) > 0) {
        forms.push({ ref: 'IHT435', description: 'Claim for Residence Nil Rate Band' });
    }

    // Nil unless claimed (IHTA 1984 s.8G(3)); an unanswered claim is listed as conditional, not as made.
    if (isIHT400 && sp?.exists === true && (sp?.unused_rnrb_percent ?? 0) > 0 && sp?.trnrb_claim !== 'no') {
        forms.push({ ref: 'IHT436', description: sp?.trnrb_claim === 'yes'
            ? 'Claim to transfer unused Residence Nil Rate Band'
            : 'Claim to transfer unused Residence Nil Rate Band (only if the transfer is claimed; not yet confirmed)' });
    }

    if (isIHT400 && (assets.bpr_assets ?? []).some(a => a.qualifies === true || a.qualifies === null)) {
        forms.push({ ref: 'IHT413', description: 'Business and partnership interests and assets' });
    }

    if (isIHT400 && (assets.apr_assets ?? []).some(a => a.qualifies === true || a.qualifies === null)) {
        forms.push({ ref: 'IHT414', description: 'Agricultural relief' });
    }

    if (isIHT400 && b?.charitable_rate_applies === true) {
        forms.push({ ref: 'IHT430', description: 'Reduced rate of Inheritance Tax' });
    }

    // Probate form
    if (willExists === 'yes') {
        forms.push({ ref: 'PA1P', description: 'Probate application (with a will)' });
    } else if (willExists === 'no') {
        forms.push({ ref: 'PA1A', description: 'Probate application (no will \u2014 letters of administration)' });
    } else {
        forms.push({ ref: 'PA1P / PA1A', description: 'Probate application (form depends on whether a will exists)' });
    }

    if (forms.length === 0) return '';

    const tableRows = forms.map(f =>
        `<tr><td><strong>${f.ref}</strong></td><td>${f.description}</td></tr>`
    ).join('');

    return section('HMRC Forms Likely Required', `
        <table class="asset-table">
            <thead><tr><th>Form</th><th>Description</th></tr></thead>
            <tbody>${tableRows}</tbody>
        </table>
        <p class="note">Form requirements are based on the estate details entered. Always verify with HMRC or a qualified adviser before submitting.</p>`);
}

function buildFormMappingSection(data: WizardData, response: ProbateResponse | null): string {
    const ihtCalc = (response?.derived as any)?.iht_calc as IHTCalculation | undefined;
    const routeKey = response?.actionPack?.recommended_pathway;
    const mappings = generateFormMappings(data.EstateAssets, data.EstateProfile, routeKey, ihtCalc);

    if (mappings.length === 0) return '';

    const ITEM_COLORS: Record<string, { bg: string; border: string; text: string }> = {
        property: { bg: '#ecfdf5', border: '#a7f3d0', text: '#047857' },
        bank_account: { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
        investment: { bg: '#faf5ff', border: '#e9d5ff', text: '#7c3aed' },
        gift: { bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
        debt: { bg: '#fef2f2', border: '#fecaca', text: '#b91c1c' },
        relief: { bg: '#f0fdfa', border: '#99f6e4', text: '#0f766e' },
        estate_summary: { bg: '#f8fafc', border: '#e2e8f0', text: '#475569' },
        probate_fact: { bg: '#eef2ff', border: '#c7d2fe', text: '#4338ca' },
        ownership: { bg: '#fffbeb', border: '#fde68a', text: '#b45309' },
        pension: { bg: '#fdf2f8', border: '#fbcfe8', text: '#be185d' },
    };

    const ITEM_LABELS: Record<string, string> = {
        property: 'Property', bank_account: 'Bank', investment: 'Investment',
        pension: 'Pension', gift: 'Gift', debt: 'Debt', ownership: 'Ownership',
        relief: 'Relief', estate_summary: 'Summary', probate_fact: 'Probate',
    };

    let html = '';
    for (const m of mappings) {
        html += `<div style="border:1px solid #e2e8f0;border-radius:8px;margin-bottom:12px;page-break-inside:avoid;overflow:hidden;">`;
        html += `<div style="background:#f8fafc;border-bottom:1px solid #e2e8f0;padding:8px 12px;">`;
        html += `<strong style="font-size:12px;color:#0f172a;">${m.formCode}</strong> <span style="font-size:11px;color:#475569;">${m.formName}</span>`;
        html += `<p style="font-size:10px;color:#64748b;margin:2px 0 0 0;">${m.triggerReason}</p>`;
        html += `</div>`;

        if (m.relatedItems.length > 0) {
            html += `<div style="padding:8px 12px;">`;
            html += `<p style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#94a3b8;margin-bottom:4px;">Related estate entries</p>`;
            for (const item of m.relatedItems) {
                const c = ITEM_COLORS[item.itemType] ?? ITEM_COLORS.estate_summary;
                const lbl = ITEM_LABELS[item.itemType] ?? item.itemType;
                html += `<div style="display:flex;align-items:flex-start;gap:6px;padding:2px 0;">`;
                html += `<span style="flex-shrink:0;font-size:7px;font-weight:800;text-transform:uppercase;color:${c.text};background:${c.bg};border:1px solid ${c.border};padding:1px 4px;border-radius:3px;margin-top:2px;">${lbl}</span>`;
                html += `<span style="font-size:10px;color:#334155;">${item.label}</span>`;
                html += `</div>`;
            }
            html += `</div>`;
        }

        if (m.suggestedEvidence.length > 0) {
            html += `<div style="padding:6px 12px 8px 12px;background:#eff6ff50;border-top:1px solid #e2e8f0;">`;
            html += `<p style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#3b82f6;margin-bottom:3px;">Evidence typically needed</p>`;
            for (const ev of m.suggestedEvidence) {
                html += `<p style="font-size:10px;color:#475569;margin:1px 0;padding-left:8px;">• ${ev}</p>`;
            }
            html += `</div>`;
        }

        html += `</div>`;
    }

    html += `<p class="note">This mapping is indicative and designed to help organise estate information. Always verify the final form requirements and contents before submission.</p>`;

    return section('HMRC Form Mapping', html);
}

function buildForeignAssetNotice(data: WizardData): string {
    if (data.EstateAssets.foreign_assets?.exists !== true) return '';

    const countries = data.EstateAssets.foreign_assets.countries ?? [];
    const countryText = countries.length > 0
        ? `Assets have been identified in: ${countries.join(', ')}.`
        : '';

    return section('Foreign Assets Detected', `
        <div class="status-box">
            <p class="status-reason">
                Assets located outside the UK may require probate or succession procedures in the country where they are located.
                This report does not calculate foreign inheritance or estate taxes.
                ${countryText}
            </p>
            <p class="status-reason" style="margin-top: 6px;">
                Seek local legal advice in each relevant jurisdiction to determine obligations, timescales, and any applicable double taxation relief.
            </p>
        </div>`);
}

function buildEvidenceChecklist(data: WizardData): string {
    const result = generateEvidenceRequests(data.EstateAssets, data.EstateProfile);
    if (result.totalCount === 0) return '';

    const CATEGORY_LABELS: Record<EvidenceCategory, string> = {
        property: 'Property',
        bank_account: 'Bank Accounts',
        investment: 'Investments',
        pension: 'Pensions',
        debt: 'Debts &amp; Liabilities',
        lifetime_gift: 'Lifetime Gifts',
        ownership: 'Ownership Verification',
        relief: 'Tax Reliefs &amp; Allowances',
        life_insurance: 'Life Insurance',
        foreign_asset: 'Foreign Assets',
        general: 'General',
    };

    const PRIORITY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
        high: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' },
        medium: { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
        low: { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af' },
    };

    // Group by category, preserving order
    const categories: EvidenceCategory[] = [];
    const grouped = new Map<EvidenceCategory, EvidenceRequest[]>();
    for (const item of result.items) {
        if (!grouped.has(item.category)) {
            categories.push(item.category);
            grouped.set(item.category, []);
        }
        grouped.get(item.category)!.push(item);
    }

    const highCount = result.items.filter(i => i.priority === 'high').length;

    let html = `<p style="font-size:11px;color:#475569;margin-bottom:14px;">${result.totalCount} document${result.totalCount !== 1 ? 's' : ''} identified based on the estate details entered${highCount > 0 ? ` \u2014 ${highCount} high priority` : ''}.</p>`;

    for (const cat of categories) {
        const items = grouped.get(cat)!;
        html += `<h3 class="asset-type">${CATEGORY_LABELS[cat] ?? cat} (${items.length})</h3>`;

        // Sub-group by group label
        const groups = [...new Set(items.map(i => i.group))];
        for (const group of groups) {
            const groupItems = items.filter(i => i.group === group);
            if (groups.length > 1) {
                html += `<p style="font-size:11px;font-weight:700;color:#334155;margin:8px 0 4px 0;">${group}</p>`;
            }
            for (const item of groupItems) {
                const pc = PRIORITY_COLORS[item.priority];
                html += `
                    <div style="display:flex;align-items:flex-start;gap:8px;padding:5px 0;border-bottom:1px solid #f1f5f9;">
                        <span style="flex-shrink:0;font-size:8px;font-weight:800;text-transform:uppercase;color:${pc.text};background:${pc.bg};border:1px solid ${pc.border};padding:1px 5px;border-radius:3px;margin-top:2px;">${item.priority}</span>
                        <div style="flex:1;min-width:0;">
                            <span style="font-size:11px;font-weight:600;color:#0f172a;">${item.document}</span>
                            <span style="font-size:10px;color:#64748b;margin-left:6px;">${item.reason}</span>
                            ${item.contactHint ? `<br/><span style="font-size:9px;color:#94a3b8;font-style:italic;">${item.contactHint}</span>` : ''}
                        </div>
                    </div>`;
            }
        }
    }

    return section('Evidence Required Before Filing', html);
}

function buildTasksSection(response: ProbateResponse | null): string {
    const tasks = response?.tasks ?? [];
    if (!tasks.length) return '';

    // Clean up any developer-style TODO language in task titles and evidence
    const cleanTodo = (text: string) =>
        text
            .replace(/\bTODO\b/gi, '')
            .replace(/\s{2,}/g, ' ')
            .trim();

    const professionaliseTitle = (title: string) => {
        let t = cleanTodo(title);
        // "Complete and Submit IHT400 TODO" → "Complete and submit IHT400"
        // "Property 1 Ownership TODO" → "Ownership verification required — Property 1"
        if (/ownership\s*$/i.test(t)) {
            const match = t.match(/^(.+?)\s+ownership\s*$/i);
            if (match) t = `Ownership verification required \u2014 ${match[1]}`;
        }
        // "Pay Inheritance Tax TODO" → "Arrange inheritance tax payment"
        if (/^pay\s+inheritance\s+tax/i.test(t)) {
            t = 'Arrange inheritance tax payment';
        }
        return t;
    };

    const cards = tasks.map((t: any) => {
        const evList = (t.evidence ?? []).map((e: any) =>
            `<li><strong>${cleanTodo(e.label)}</strong>${e.description ? `: ${cleanTodo(e.description)}` : ''}${e.example ? `<br/><span class="example">${e.example}</span>` : ''}</li>`
        ).join('');

        const statusLabel = (t.status ?? '')
            .replace(/_/g, ' ')
            .replace(/\bTODO\b/gi, 'Action required')
            .replace(/^todo$/i, 'Action required');

        return `
            <div class="task-card">
                <div class="task-header">
                    <span class="task-title">${professionaliseTitle(t.title)}</span>
                    <span class="task-badge">${statusLabel}</span>
                </div>
                ${evList ? `<ul class="ev-list">${evList}</ul>` : ''}
            </div>`;
    }).join('');

    return section('Required Actions &amp; Evidence', cards);
}

function buildDeadlinesSection(data: WizardData, response: ProbateResponse | null): string {
    const dodStr = data.EstateProfile.deceased?.date_of_death;
    if (!dodStr) return '';
    const dod = new Date(dodStr);
    if (isNaN(dod.getTime())) return '';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const routeKey = response?.actionPack?.recommended_pathway;
    const eom = new Date(dod.getFullYear(), dod.getMonth() + 1, 0);

    const addM = (base: Date, m: number) => new Date(base.getFullYear(), base.getMonth() + m + 1, 0);
    const daysFrom = (to: Date) => Math.floor((to.getTime() - today.getTime()) / 86400000);
    const fd = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    const rows: [string, string, string][] = []; // [title, date, note]

    if (routeKey !== 'POST_2022_EXCEPTED') {
        const d = addM(eom, 6);
        const days = daysFrom(d);
        rows.push(['IHT Payment Deadline', fd(d), days < 0 ? `Overdue by ${Math.abs(days)} days \u2014 interest accruing` : `${days} days remaining`]);
    }

    if (routeKey === 'POST_2022_IHT400') {
        const d = addM(eom, 12);
        const days = daysFrom(d);
        rows.push(['IHT400 Submission Deadline', fd(d), days < 0 ? `Overdue by ${Math.abs(days)} days` : `${days} days remaining`]);
    }

    const probD = addM(eom, 12);
    const probDays = daysFrom(probD);
    rows.push(['Probate Application (recommended by)', fd(probD), probDays < 0 ? `${Math.abs(probDays)} days past recommended window` : `${probDays} days remaining`]);

    const tableRows = rows.map(([t, d, n]) => `
        <tr><td><strong>${t}</strong></td><td>${d}</td><td style="color:#64748b;font-size:10px;">${n}</td></tr>`).join('');

    return section('Key Deadlines', `
        <table class="asset-table">
            <thead><tr><th>Deadline</th><th>Date</th><th>Status</th></tr></thead>
            <tbody>${tableRows}</tbody>
        </table>
        <p class="note">Dates calculated from date of death per HMRC rules (IHTA 1984 s226, s216). Verify with a qualified adviser.</p>`);
}

// ---------------------------------------------------------------------------
// 2. ESTATE RISK SUMMARY (new section)
// ---------------------------------------------------------------------------

function buildRiskSummarySection(data: WizardData, response: ProbateResponse | null): string {
    const ihtCalc = (response?.derived as any)?.iht_calc as IHTCalculation | undefined;
    const assets = data.EstateAssets;
    const profile = data.EstateProfile;

    // Merge IHT risk flags and probate risks (same logic as RiskAlertsPanel)
    const ihtFlags = assessIHTRiskFlags(ihtCalc ?? null, assets, profile);
    const probateAssessment = assessProbateRisks(ihtCalc, assets, profile);

    type SeverityKey = 'High' | 'Medium' | 'Low';
    interface ReportRisk {
        title: string;
        severity: SeverityKey;
        reason: string;
        nextStep: string;
    }

    const risks: ReportRisk[] = [];
    const seenTopics = new Set<string>();

    for (const flag of ihtFlags) {
        risks.push({
            title: flag.title,
            severity: flag.severity,
            reason: flag.why,
            nextStep: flag.nextStep,
        });
        seenTopics.add(flag.id);
    }

    const sevMap: Record<string, SeverityKey> = { high: 'High', medium: 'Medium', low: 'Low' };
    for (const risk of probateAssessment.risks) {
        if (seenTopics.has(risk.id)) continue;
        risks.push({
            title: risk.name,
            severity: sevMap[risk.severity] ?? 'Medium',
            reason: risk.why,
            nextStep: risk.action,
        });
        seenTopics.add(risk.id);
    }

    // Phase 1.5 — review-flag risks sourced from new Basics / Discovery /
    // Complexity signals. Appended after server-derived flags so they don't
    // duplicate anything the existing risk detectors emit. Severity = Medium
    // (review prompts, not confirmed risks).
    const trustsUncertain =
        assets.trusts_or_life_interest?.exists !== true &&
        assets.trusts_or_life_interest?.unsure === true;
    if (trustsUncertain) {
        risks.push({
            title: 'Trust involvement uncertain',
            severity: 'Medium',
            reason: 'Trust involvement is uncertain — check whether IHT400/IHT418 or specialist advice is required.',
            nextStep: 'Confirm whether any assets were held in trust or whether the deceased created or benefited from a trust.',
        });
    }

    const originOutsideUk = profile.deceased?.origin_outside_uk;
    if (originOutsideUk === 'yes' || originOutsideUk === 'unsure') {
        risks.push({
            title: 'Domicile / residence may need review',
            severity: 'Medium',
            reason: 'Domicile/residence may need review because the deceased was originally from outside the UK or this is uncertain.',
            nextStep: 'Confirm domicile and long-residence position. Deemed-domicile rules can affect IHT scope.',
        });
    }

    const passedToSpouse = profile.family?.assets_passed_to_spouse === 'yes';
    const spouseAlive = profile.family?.current_spouse_alive;
    if (passedToSpouse && (spouseAlive === 'no' || spouseAlive === 'unsure')) {
        risks.push({
            title: 'Spouse exemption assumption needs confirmation',
            severity: 'Medium',
            reason: 'Spouse exemption may not apply unless there was a surviving spouse or civil partner at death.',
            nextStep: 'Confirm whether a spouse or civil partner survived the deceased before relying on spouse exemption.',
        });
    }

    // Wave 4 — gift with reservation. Mention affected property labels when the
    // signal originates from PropertyStep so the report says exactly which
    // property is in question. We deliberately don't auto-clear the
    // estate-level flag elsewhere; this read uses the derived helper.
    const gwrProps = (assets.properties ?? []).filter(p => p.gift_with_reservation === 'yes');
    const gwrSignalled =
        profile.iht_details?.trust_screening?.has_gift_with_reservation === true ||
        gwrProps.length > 0;
    // Skip if the existing risk detectors already raised something on this
    // topic — `seenTopics` tracks the server-derived flag IDs and labels.
    const gwrAlreadySeen = Array.from(seenTopics).some(id => /gwr|reservation/i.test(id));
    if (gwrSignalled && !gwrAlreadySeen) {
        const propBit = gwrProps.length > 0
            ? gwrProps.map((p, i) => {
                  const idx = (assets.properties ?? []).indexOf(p);
                  const label = (p.label?.trim() || p.address?.trim() || `Property ${idx + 1}`);
                  return escapeHtml(label);
              }).join(', ')
            : '';
        risks.push({
            title: 'Possible gift with reservation of benefit',
            severity: 'High',
            reason: propBit
                ? `Potential gift with reservation: ${propBit} ${gwrProps.length > 1 ? 'were' : 'was'} marked as given away while the deceased continued to benefit. This may keep the asset within the estate for IHT (s102 IHTA 1984).`
                : 'A gift-with-reservation flag has been raised. The deceased may have given away an asset but continued to benefit from it, which may keep it within the estate for IHT (s102 IHTA 1984).',
            nextStep: 'Review the affected asset(s) and consider specialist advice. IHT400 and IHT418 may apply.',
        });
    }

    // Sort by severity
    const sevOrder: SeverityKey[] = ['High', 'Medium', 'Low'];
    risks.sort((a, b) => sevOrder.indexOf(a.severity) - sevOrder.indexOf(b.severity));

    if (risks.length === 0) {
        return section('Estate Risk Summary', `
            <div class="status-box">
                <div class="status-label" style="color:#059669">No significant risks identified</div>
                <p class="status-reason">Based on the information provided, no issues were identified that would typically delay or complicate estate administration.</p>
            </div>`, 'Risks, Evidence &amp; Actions');
    }

    // Show top 5 risks
    const topRisks = risks.slice(0, 5);
    const sevStyles: Record<SeverityKey, { bg: string; border: string; color: string }> = {
        High: { bg: '#fef2f2', border: '#fecaca', color: '#991b1b' },
        Medium: { bg: '#fffbeb', border: '#fde68a', color: '#92400e' },
        Low: { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af' },
    };

    const riskCards = topRisks.map(r => {
        const s = sevStyles[r.severity];
        return `
            <div class="risk-card" style="background:${s.bg};border:1px solid ${s.border};border-radius:8px;padding:12px 14px;margin-bottom:10px;page-break-inside:avoid;">
                <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:4px;">
                    <strong style="font-size:12px;color:#0f172a;">${r.title}</strong>
                    <span style="font-size:9px;font-weight:800;text-transform:uppercase;color:${s.color};background:white;padding:2px 8px;border-radius:4px;border:1px solid ${s.border};white-space:nowrap;margin-left:8px;">${r.severity}</span>
                </div>
                <p style="font-size:11px;color:#334155;margin:4px 0;line-height:1.5;">${r.reason}</p>
                <p style="font-size:11px;color:#475569;margin:4px 0 0 0;line-height:1.5;"><strong>Next action:</strong> ${r.nextStep}</p>
            </div>`;
    }).join('');

    const highCount = risks.filter(r => r.severity === 'High').length;
    const medCount = risks.filter(r => r.severity === 'Medium').length;
    const lowCount = risks.filter(r => r.severity === 'Low').length;
    const summaryParts = [];
    if (highCount > 0) summaryParts.push(`${highCount} high priority`);
    if (medCount > 0) summaryParts.push(`${medCount} medium`);
    if (lowCount > 0) summaryParts.push(`${lowCount} low`);

    const additionalNote = risks.length > 5
        ? `<p class="note" style="margin-top:8px">Showing the ${topRisks.length} most significant risks. ${risks.length - 5} additional items were identified and are available in the full analysis.</p>`
        : '';

    return section('Estate Risk Summary', `
        <p style="font-size:11px;color:#475569;margin-bottom:12px;">${risks.length} risk${risks.length !== 1 ? 's' : ''} identified: ${summaryParts.join(', ')}.</p>
        ${riskCards}
        ${additionalNote}`, 'Risks, Evidence &amp; Actions');
}

// ---------------------------------------------------------------------------
// 3. CALCULATION METHOD (new trust-building section)
// ---------------------------------------------------------------------------

function buildCalculationMethodSection(response: ProbateResponse | null): string {
    const ihtCalc = (response?.derived as any)?.iht_calc;
    const b = ihtCalc?.breakdown;

    const bullets: string[] = [
        'Nil Rate Band applied at current tax-year threshold',
    ];

    if ((b?.rnrb_available ?? 0) > 0 || (b?.rnrb_before_taper ?? 0) > 0) {
        bullets.push('Residence Nil Rate Band applied where a qualifying residence passes to direct descendants');
    }

    if ((b?.gifts_chargeable_total ?? 0) > 0) {
        bullets.push('Lifetime gifts within 7 years reduce the Nil Rate Band before estate allowances');
    }

    if ((b?.taper_relief_total ?? 0) > 0) {
        bullets.push('Taper relief applies only to tax on gifts above the available Nil Rate Band');
    }

    const rate = b?.iht_rate_applied ?? 0.4;
    if (b?.charitable_rate_applies) {
        bullets.push('Inheritance Tax rate applied at 36% (reduced rate for estates leaving 10% or more to charity)');
    } else {
        bullets.push(`Inheritance Tax rate assumed at ${(rate * 100).toFixed(0)}% unless special relief applies`);
    }

    if ((b?.spouse_exemption_total ?? 0) > 0) {
        if (b?.ipdi_detected) {
            bullets.push('Assets passing to a surviving spouse or civil partner are treated as exempt, including assets held in a life interest trust (IPDI)');
        } else {
            bullets.push('Assets passing to a surviving spouse or civil partner are treated as exempt');
        }
    }

    if ((b?.bpr_relief_total ?? 0) > 0 || (b?.apr_relief_total ?? 0) > 0) {
        bullets.push('Business Property Relief and/or Agricultural Property Relief applied where eligibility was indicated');
    }

    bullets.push('Figures based on user-entered assumptions and available estate details');

    const taxYear = b?.tax_year ? ` for the tax year ${b.tax_year}` : '';
    const listItems = bullets.map(b => `<li>${b}</li>`).join('');

    return section('Calculation Method', `
        <ul style="margin: 0 0 12px 18px; padding: 0; font-size: 11px; color: #334155; line-height: 1.8;">
            ${listItems}
        </ul>
        <p class="note">Calculations are intended to reflect HMRC inheritance tax rules${taxYear}, but should be verified with a qualified adviser before filing.</p>`);
}

// ---------------------------------------------------------------------------
// 7. ESTATE ADMINISTRATION ROADMAP (new final section)
// ---------------------------------------------------------------------------

function buildComplexitySection(data: WizardData, response: ProbateResponse | null): string {
    const ihtCalc = (response?.derived as any)?.iht_calc as IHTCalculation | undefined;
    const assessment = assessComplexity(ihtCalc, data.EstateAssets, data.EstateProfile);

    const LEVEL_COLORS: Record<ComplexityLevel, { bg: string; border: string; text: string; barFill: string }> = {
        Low: { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', barFill: '#22c55e' },
        Medium: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', barFill: '#f59e0b' },
        High: { bg: '#fff7ed', border: '#fed7aa', text: '#9a3412', barFill: '#f97316' },
        'Very High': { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', barFill: '#ef4444' },
    };
    const c = LEVEL_COLORS[assessment.level];
    const levelIdx = { Low: 0, Medium: 1, High: 2, 'Very High': 3 }[assessment.level];

    // Visual bar
    const barSegments = [0, 1, 2, 3].map(i =>
        `<div style="flex:1;height:8px;border-radius:4px;background:${i <= levelIdx ? c.barFill : '#e2e8f0'};"></div>`
    ).join('');

    // Factor rows
    const factorRows = assessment.factors.map(f =>
        `<div style="display:flex;align-items:flex-start;gap:6px;padding:4px 0;border-bottom:1px solid #f1f5f9;">
            <span style="font-size:11px;font-weight:600;color:#334155;flex:1;">${f.label}</span>
            <span style="font-size:10px;font-family:monospace;color:#94a3b8;white-space:nowrap;">+${f.points}</span>
        </div>`
    ).join('');

    return section('Probate Complexity', `
        <div style="display:flex;align-items:center;gap:12px;margin-bottom:12px;">
            <span style="display:inline-block;padding:4px 14px;border-radius:20px;font-size:13px;font-weight:800;color:${c.text};background:${c.bg};border:1px solid ${c.border};">${assessment.level}</span>
            <span style="font-size:11px;color:#94a3b8;font-family:monospace;">Score: ${assessment.score}</span>
        </div>

        <div style="display:flex;gap:3px;margin-bottom:6px;">${barSegments}</div>
        <div style="display:flex;justify-content:space-between;font-size:8px;color:#94a3b8;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:14px;">
            <span>Low</span><span>Medium</span><span>High</span><span>Very High</span>
        </div>

        <p style="font-size:12px;color:#334155;line-height:1.6;margin-bottom:14px;">${assessment.summary}</p>

        ${assessment.factors.length > 0 ? `
            <p style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#94a3b8;margin-bottom:6px;">Factors increasing complexity</p>
            ${factorRows}
        ` : ''}

        <p class="note" style="margin-top:12px;">This score estimates how difficult the estate may be to administer based on the information entered. It is a workflow aid, not a legal classification.</p>
    `);
}

function buildAdministrationRoadmapSection(): string {
    const steps = [
        { step: 'Confirm date-of-death asset values', detail: 'Obtain formal valuations for properties, investments, and all financial assets as at the date of death.' },
        { step: 'Verify gifts, ownership, and supporting evidence', detail: 'Gather documentation for lifetime gifts, confirm ownership type for jointly held assets, and collect trust deeds where relevant.' },
        { step: 'Arrange inheritance tax payment', detail: 'If IHT is due, arrange payment to HMRC. Consider the instalment option for property or business assets if liquid funds are insufficient.' },
        { step: 'Prepare and submit IHT return with required schedules', detail: 'Complete the appropriate IHT form (IHT400 or excepted estate route) with all supporting schedules.' },
        { step: 'Apply for probate', detail: 'Submit the probate application (PA1P with a will, or PA1A for intestacy) along with the IHT summary form IHT421.' },
        { step: 'Collect in estate assets', detail: 'Once the grant of probate is issued, notify banks, insurers, and registrars to release assets to the executor.' },
        { step: 'Settle debts and administration costs', detail: 'Pay outstanding debts, funeral expenses, and professional fees from the estate before distribution.' },
        { step: 'Distribute the estate and prepare estate accounts', detail: 'Distribute the remaining estate in accordance with the will or intestacy rules, and prepare final estate accounts for beneficiaries.' },
    ];

    const stepRows = steps.map((s, i) => `
        <div class="roadmap-step">
            <div class="roadmap-number">${i + 1}</div>
            <div class="roadmap-content">
                <strong style="font-size:12px;color:#0f172a;">${s.step}</strong>
                <p style="font-size:11px;color:#475569;margin:2px 0 0 0;line-height:1.5;">${s.detail}</p>
            </div>
        </div>`).join('');

    return section('Estate Administration Roadmap', `
        <p class="note" style="margin-bottom:12px;">A typical sequence for estate administration. Timescales and requirements vary depending on the complexity of the estate. This is a practical guide, not legal advice.</p>
        ${stepRows}`);
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const STYLES = `
    @page { size: A4 portrait; margin: 2cm; }
    * { box-sizing: border-box; }
    body {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 12px;
        line-height: 1.5;
        color: #1e293b;
        margin: 0;
        padding: 0;
        background: #fff;
    }
    .container { max-width: 21cm; margin: 0 auto; padding: 0; }

    /* Disclaimer */
    .disclaimer {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        padding: 8px 16px;
        font-size: 10px;
        color: #64748b;
        text-align: center;
        margin-bottom: 24px;
    }

    /* Header */
    .report-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        border-bottom: 3px solid #0f172a;
        padding-bottom: 12px;
        margin-bottom: 24px;
    }
    .report-title { font-size: 26px; font-weight: 800; color: #0f172a; margin: 0; }
    .report-subtitle { font-size: 14px; color: #64748b; font-weight: 500; margin-top: 2px; }
    .report-meta { text-align: right; font-size: 10px; color: #94a3b8; line-height: 1.8; }

    /* Page markers */
    .page-marker {
        font-size: 8px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: #94a3b8;
        border-bottom: 1px solid #e2e8f0;
        padding-bottom: 4px;
        margin-bottom: 14px;
    }

    /* Sections */
    .section { margin-bottom: 28px; page-break-inside: avoid; }
    h2 {
        font-size: 13px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #0f172a;
        border-left: 4px solid #0f172a;
        padding-left: 10px;
        margin: 0 0 12px 0;
        page-break-after: avoid;
    }
    h3.asset-type {
        font-size: 11px;
        font-weight: 700;
        color: #475569;
        margin: 14px 0 6px 0;
        page-break-after: avoid;
    }

    /* Overview grid */
    .overview-grid {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 12px;
        margin-bottom: 16px;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 16px;
    }
    .overview-cell { }
    .overview-label {
        font-size: 9px;
        font-weight: 700;
        text-transform: uppercase;
        color: #94a3b8;
        letter-spacing: 0.06em;
        margin-bottom: 4px;
    }
    .overview-value {
        font-size: 15px;
        font-weight: 800;
        color: #0f172a;
    }
    .overview-narrative {
        font-size: 12px;
        color: #334155;
        line-height: 1.6;
        margin: 0;
        padding: 10px 14px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
    }

    /* Key-value grid */
    .kv-grid { display: grid; gap: 12px; margin-bottom: 8px; }
    .kv-label { font-size: 9px; font-weight: 700; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.06em; margin-bottom: 2px; }
    .kv-value { font-size: 13px; font-weight: 700; color: #0f172a; }

    /* Calc table */
    .calc-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px; }
    .calc-table td { padding: 3px 6px 3px 0; color: #334155; }
    .calc-table .val-cell { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .calc-table .indent { padding-left: 20px; color: #64748b; }
    .calc-table .bold-row { background: #f8fafc; }
    .calc-table .bold-cell { font-weight: 700; color: #0f172a; }
    .calc-table .positive { color: #059669; }
    .rule { border-top: 1px solid #cbd5e1; margin: 3px 0; }
    .double-rule { border-top: 3px double #94a3b8; margin: 4px 0; }

    /* Asset tables */
    .asset-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px; }
    .asset-table th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; font-weight: 700; border-bottom: 1px solid #e2e8f0; padding: 4px 6px 4px 0; }
    .asset-table td { padding: 5px 6px 5px 0; border-bottom: 1px solid #f1f5f9; color: #334155; }
    .num-cell { text-align: right; font-variant-numeric: tabular-nums; }

    /* Status box */
    .status-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; }
    .status-label { font-size: 14px; font-weight: 800; color: #0f172a; margin-bottom: 6px; }
    .status-reason { font-size: 11px; color: #475569; margin: 0; line-height: 1.6; }

    /* Task cards */
    .task-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; margin-bottom: 10px; page-break-inside: avoid; }
    .task-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; }
    .task-title { font-size: 13px; font-weight: 700; color: #0f172a; }
    .task-badge { font-size: 9px; font-weight: 800; text-transform: uppercase; background: #f1f5f9; color: #64748b; padding: 2px 6px; border-radius: 4px; white-space: nowrap; margin-left: 8px; }
    .ev-list { margin: 6px 0 0 16px; padding: 0; font-size: 11px; color: #334155; line-height: 1.7; }
    .example { color: #94a3b8; font-size: 10px; font-style: italic; }

    /* Roadmap */
    .roadmap-step {
        display: flex;
        gap: 12px;
        align-items: flex-start;
        padding: 10px 0;
        border-bottom: 1px solid #f1f5f9;
        page-break-inside: avoid;
    }
    .roadmap-step:last-child { border-bottom: none; }
    .roadmap-number {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: #0f172a;
        color: #fff;
        font-size: 12px;
        font-weight: 800;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .roadmap-content { flex: 1; min-width: 0; }

    /* Misc */
    .note { font-size: 10px; color: #64748b; font-style: italic; margin: 6px 0 10px 0; }

    /* Footer */
    .report-footer {
        margin-top: 40px;
        padding-top: 12px;
        border-top: 1px solid #e2e8f0;
        font-size: 9px;
        color: #94a3b8;
        text-align: center;
        page-break-inside: avoid;
    }

    /* Pro case header — shown only when proMetadata is supplied */
    .pro-case-header {
        margin: 0 0 18px 0;
        padding: 14px 16px;
        border: 1px solid #cbd5e1;
        border-left: 3px solid #1e293b;
        background: #f8fafc;
        page-break-inside: avoid;
    }
    .pro-case-header__eyebrow {
        font-size: 9px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: #475569;
        font-weight: 600;
        margin-bottom: 8px;
    }
    .pro-case-header__table {
        width: 100%;
        border-collapse: collapse;
        font-size: 11px;
    }
    .pro-case-header__table th {
        text-align: left;
        font-weight: 500;
        color: #64748b;
        padding: 2px 12px 2px 0;
        white-space: nowrap;
        width: 1%;
        vertical-align: top;
    }
    .pro-case-header__table td {
        color: #0f172a;
        padding: 2px 0;
        font-weight: 500;
    }

    @media print {
        body { padding: 0; }
        .no-print { display: none !important; }
        .overview-grid { grid-template-columns: repeat(5, 1fr); }
    }
`;

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

function computeVisibleEstateTotal(data: WizardData): number {
    const a = data.EstateAssets;
    let total = 0;
    for (const p of a.properties ?? []) {
        const fullVal = Number(p.estimated_value) || 0;
        const mortgage = p.has_mortgage ? (Number(p.mortgage_balance) || 0) : 0;
        const netEquity = Math.max(0, fullVal - mortgage);
        const share = p.deceased_share_percent ?? 100;
        total += Math.round(netEquity * share / 100);
    }
    for (const b of a.bank_accounts ?? []) {
        const fullBal = Number(b.estimated_value) || 0;
        total += b.ownership === 'joint' ? fullBal / 2 : fullBal;
    }
    for (const i of a.investments ?? []) {
        total += Number(i.estimated_value) || 0;
    }
    for (const p of a.pensions ?? []) {
        if (p.in_estate !== false) total += Number(p.estimated_value) || 0;
    }
    if (a.life_insurance?.exists === true && a.life_insurance.written_in_trust !== true) {
        total += Number(a.life_insurance.estimated_value) || 0;
    }
    return total;
}

/**
 * Assemble the ordered report sections into the final body HTML. Exported and
 * pure (no DOM, no window) so the figure-suppression contract can be pinned in
 * node:test without rendering.
 *
 * Figure suppression mirrors the on-screen panels via selectReportPanels:
 *   • pre-IHT (PRE_IHT_DEATH_DATE) → showFigures=false: the IHT figure sections
 *     (overview, summary, calc breakdown, calculation method) are dropped and a
 *     banner explains that the regime cannot be computed. No confident wrong
 *     number reaches the PDF.
 *   • pre-2017 → showFigures=true, showBanner=true: figures render *indicatively*
 *     alongside the banner — unchanged behaviour plus the warning.
 *   • supported → figures render, no banner.
 * Non-figure sections (deceased details, assets, probate status, deadlines,
 * forms, risks, roadmap…) are always included regardless of regime.
 */
export function assembleReportBody(
    data: WizardData,
    response: ProbateResponse | null,
): string {
    const ihtCalc = (response?.derived as any)?.iht_calc as IHTCalculation | undefined;
    const panels = selectReportPanels(ihtCalc);

    return [
        // Date-of-death banner (pre-IHT / pre-2017) — first so it frames the report.
        buildUnsupportedDateBanner(panels),
        // Trust estate-inclusion gap (WP1) — frames the figures as provisional.
        panels.showFigures ? buildTrustEstateInclusionBanner(ihtCalc) : '',
        // 1. Estate Overview (new executive summary) — figures
        panels.showFigures ? buildEstateOverviewSection(data, response) : '',
        // 2. Deceased Details
        buildDeceasedSection(data),
        // 3. Inheritance Tax Summary — figures
        panels.showFigures ? buildIHTSummarySection(data, response) : '',
        // 4. IHT Calculation Breakdown — figures
        panels.showFigures ? buildCalcBreakdownSection(data, response) : '',
        // 5. Key Deadlines
        buildDeadlinesSection(data, response),
        // 6. HMRC Forms Likely Required
        buildHMRCFormsSection(data, response),
        // 6b. HMRC Form Mapping (which items feed each form)
        buildFormMappingSection(data, response),
        // 7. Probate Status
        buildProbateSection(data, response),
        // 7b. Trusts & Special Arrangements (IPDI)
        buildTrustArrangementsSection(data, response),
        // 8. Asset Summary
        buildAssetSection(data),
        // 9. Lifetime Gifts
        buildGiftsSection(data),
        // 10. Income Assessment
        buildIncomeAssessmentSection(data),
        // 11. Foreign Assets
        buildForeignAssetNotice(data),
        // 12. Probate Complexity (new)
        buildComplexitySection(data, response),
        // 13. Estate Risk Summary (new)
        buildRiskSummarySection(data, response),
        // 14. Calculation Method (new trust-building section) — describes the figures
        panels.showFigures ? buildCalculationMethodSection(response) : '',
        // 14. Evidence Checklist
        buildEvidenceChecklist(data),
        // 15. Required Actions & Evidence
        buildTasksSection(response),
        // 16. Estate Administration Roadmap (new final section)
        buildAdministrationRoadmapSection(),
    ].filter(Boolean).join('\n');
}

export function generateAndOpenProbateReport(
    data: WizardData,
    response: ProbateResponse | null,
    proMetadata?: ProReportMetadata | null,
) {
    const d = data.EstateProfile.deceased ?? {};
    const caseName = [d.firstName, d.lastName].filter(Boolean).join(' ') || 'Estate';
    const dod = fmtDate(d.date_of_death);
    const proHeaderHtml = buildProCaseHeader(proMetadata);

    // Generate a case reference from today's date + random suffix
    const now = new Date();
    const yr = now.getFullYear();
    const mo = String(now.getMonth() + 1).padStart(2, '0');
    const dy = String(now.getDate()).padStart(2, '0');
    const rnd = String(Math.floor(Math.random() * 999) + 1).padStart(3, '0');
    const caseRef = `PT-${yr}-${mo}${dy}-${rnd}`;

    // Validate: gross estate from server calc must reconcile with visible asset total
    const ihtCalc = (response?.derived as any)?.iht_calc;
    const grossFromCalc = ihtCalc?.breakdown?.gross_value;
    if (grossFromCalc != null) {
        const visibleTotal = computeVisibleEstateTotal(data);
        if (Math.abs(grossFromCalc - visibleTotal) > 1) {
            console.error(
                `Estate total mismatch between calculation and report. Calc gross: ${grossFromCalc}, Visible asset sum: ${visibleTotal}, Diff: ${grossFromCalc - visibleTotal}`
            );
        }
    }

    const body = assembleReportBody(data, response);

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>Probate Report \u2014 ${caseName}</title>
    <style>${STYLES}</style>
</head>
<body>
<div class="container">
    <div class="disclaimer">
        This document is a working summary produced by the EstateClarity. It is not legal or tax advice. Figures are based on the information provided and should be verified before filing.
    </div>

    ${proHeaderHtml}

    <div class="report-header">
        <div>
            <div class="report-title">Probate Report</div>
            <div class="report-subtitle">${caseName}</div>
        </div>
        <div class="report-meta">
            Case Reference: ${caseRef}<br/>
            Date of Death: ${dod}<br/>
            Generated: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
            ${data.EstateProfile.family?.executor_name ? `Prepared for: ${escapeHtml(data.EstateProfile.family.executor_name)}<br/>` : ''}
            EstateClarity
        </div>
    </div>

    ${body}

    <div class="report-footer">
        This document is a working summary produced by the EstateClarity. It is not legal or tax advice.
        Figures are estimates based on information provided and may change as more details are confirmed.
        Always verify with a qualified professional before filing.<br/>
        Produced by EstateClarity &bull; ${new Date().getFullYear()}
    </div>
</div>
<script>
    // Auto-trigger print dialog so user can Save as PDF
    window.addEventListener('load', function() {
        setTimeout(function() { window.print(); }, 400);
    });
</script>
</body>
</html>`;

    // Use Blob URL with explicit UTF-8 encoding to guarantee £ and special chars render correctly
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) {
        // Popup blocked — fall back to download
        const a = document.createElement('a');
        a.href = url;
        a.download = `Probate_Report_${caseName.replace(/\s+/g, '_')}.html`;
        a.click();
    }
    // Revoke URL after a delay to allow the window/download to load
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}
